import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { REVIEWS } from "../mock";
import { Star, ShieldCheck, Truck, Leaf, FlaskConical, ArrowRight } from "lucide-react";

export const OrderSection = () => {
  const { t } = useApp();
  const [time, setTime] = useState({ h: "00", m: "00", s: "00" });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const target = new Date();
      target.setHours(15, 0, 0, 0);
      if (now > target) target.setDate(target.getDate() + 1);
      const diff = Math.max(0, target - now);
      const h = Math.floor(diff / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1e3);
      const p = (n) => String(n).padStart(2, "0");
      setTime({ h: p(h), m: p(m), s: p(s) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const perks = [t.perk1, t.perk2, t.perk3, t.perk4];

  return (
    <section className="py-8">
      <div className="max-w-[1400px] mx-auto px-5">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#3A2C28] text-[#F4E6DE] grain">
          <div className="relative px-8 md:px-16 py-16 md:py-20">
            <div className="flex items-center gap-3 text-[11px] tracking-label uppercase text-[#C9A99B]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7EC79C] animate-pulse" />
              {t.orderKicker}
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-10 items-end">
              <div>
                <h2 className="font-serif text-4xl md:text-6xl leading-[0.98]">
                  <span className="block">{t.orderTitle}</span>
                  <span className="block italic text-[#E3A99A]">{t.orderTitle2}</span>
                </h2>
                <p className="mt-6 max-w-md text-sm text-[#D4BEB3] leading-relaxed">{t.orderText}</p>
              </div>
              <div>
                <p className="text-[11px] tracking-label uppercase text-[#C9A99B] mb-3">{t.nextDispatch}</p>
                <div className="flex gap-3">
                  {[time.h, time.m, time.s].map((val, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <div className="h-16 w-16 md:h-20 md:w-20 rounded-xl bg-[#4A3A34] border border-[#5A473F] flex items-center justify-center">
                        <span className="font-serif text-3xl md:text-4xl">{val}</span>
                      </div>
                      <span className="text-[10px] tracking-label uppercase text-[#A6897C] mt-2">
                        {["uur", "min", "sec"][i]}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#E3A99A] text-[#3A2C28] text-[12px] tracking-label uppercase hover:bg-[#F4E6DE]"
                >
                  {t.orderCta}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-[#5A473F] grid grid-cols-2 md:grid-cols-4 gap-4">
              {perks.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] text-[#D4BEB3]">
                  <span className="text-[#7EC79C]">✓</span>
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Manifesto = () => {
  const { t } = useApp();
  const icons = [FlaskConical, Leaf, ShieldCheck, Truck];
  return (
    <section id="manifesto" className="py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-5">
        <span className="text-[11px] tracking-label uppercase text-[#B4715C]">{t.manifestoKicker}</span>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#3A2C28] max-w-2xl leading-tight">
          {t.manifestoTitle}
        </h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.chapters.map((c, i) => {
            const Icon = icons[i];
            return (
              <div
                key={i}
                className="group relative bg-white rounded-2xl border border-[#EEDDD3] p-7 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(160,100,80,0.35)] transition-all duration-500"
              >
                <span className="text-[10px] tracking-label uppercase text-[#C0A99E]">
                  {`CHAPTER · 0${i + 1}`}
                </span>
                <div className="mt-6 h-12 w-12 rounded-full bg-[#F4E6DE] flex items-center justify-center text-[#B4715C] group-hover:bg-[#B4715C] group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-[#3A2C28] leading-tight">{c.t}</h3>
                <p className="mt-3 text-sm text-[#6E5A52] leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Reviews = () => {
  const { t, lang } = useApp();
  return (
    <section className="py-16 bg-[#F3E1D8]">
      <div className="max-w-[1400px] mx-auto px-5">
        <h2 className="font-serif text-3xl md:text-4xl text-[#3A2C28] text-center">{t.reviewsTitle}</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#EEDDD3] p-7">
              <div className="flex gap-1">
                {[...Array(r.rating)].map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-[#D89A6E] text-[#D89A6E]" />
                ))}
              </div>
              <p className="mt-4 font-serif text-xl text-[#3A2C28] leading-snug italic">“{r[lang]}”</p>
              <p className="mt-4 text-[11px] tracking-label uppercase text-[#8A756C]">{r.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Payments = () => {
  const { t } = useApp();
  const methods = ["iDEAL", "VISA", "Mastercard", "KLARNA", "PayPal", "SEPA"];
  return (
    <section className="py-20 md:py-24">
      <div className="max-w-[1400px] mx-auto px-5 text-center">
        <span className="text-[11px] tracking-label uppercase text-[#B4715C]">{t.payKicker}</span>
        <h2 className="mt-4 font-serif text-3xl md:text-5xl text-[#3A2C28] max-w-2xl mx-auto leading-tight">
          {t.payTitle}
        </h2>
        <p className="mt-5 text-sm text-[#6E5A52] max-w-md mx-auto">{t.payText}</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {methods.map((m) => (
            <div
              key={m}
              className="h-14 min-w-[96px] px-6 rounded-xl bg-white border border-[#EEDDD3] flex items-center justify-center text-[13px] tracking-label uppercase text-[#5A4740]"
            >
              {m}
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-[11px] tracking-label uppercase text-[#A38F86]">
          <span>[ SSL ] End-to-end encrypted</span>
          <span>[ EU ] PSD2 compliant</span>
          <span>[ PCI ] DSS Level 1 · Stripe</span>
        </div>
      </div>
    </section>
  );
};
